import { ErrorMessage, Field, Form as FormikForm, Formik, FormikProps } from 'formik';
import React, { useState } from 'react';
import { Form, FormControl, Image, Stack } from 'react-bootstrap';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import SFCLogoMD from '../../assets/images/sticky-fingers-logo-stacked-2.png';
import { SimpleButton } from '../../components/buttons/SimpleButton';
import { useToast } from '../../context';
import { useForgotPasswordMutation } from '../../services/endpoints/people/user';
import { PageMeta } from '../../components/meta/PageMeta';
import { getErrorMessage } from '../../utils/utils';
import { FadeIn } from '../animations/FadeIn';

type Props = {};

interface FormValues {
  email: string;
}

const ForgotPassword: React.FC<Props> = () => {
  const navigate: NavigateFunction = useNavigate();

  const [forgotPassword] = useForgotPasswordMutation();

  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const { addToast } = useToast();

  const initialValues: FormValues = {
    email: ''
  };

  const validationSchema = Yup.object().shape({
    email: Yup.string().required('This field is required!')
  });

  const sendInstructions = (formValue: FormValues) => {
    const { email } = formValue;

    setLoading(true);
    setMessage('');
    forgotPassword(email)
      .unwrap()
      .then((response) => {
        setMessage(response.message);
      })
      .catch((error) => {
        addToast(getErrorMessage(error), 'error');
      });

    setLoading(false);
  };

  return (
    <FadeIn className="d-flex flex-column align-content-center flex-wrap">
      <PageMeta title="Forgot Password" />
      <div className="pb-4">
        <Image
          fluid
          className="d-block d-sm-none"
          src={SFCLogoMD}
          alt="Sticky Fingers Logo"
          onClick={() => navigate('/')}
        />
        <Image
          className="d-none d-sm-inline-flex"
          src={SFCLogoMD}
          style={{ maxWidth: 343 }}
          alt="Sticky Fingers Logo"
          onClick={() => navigate('/')}
        />
      </div>

      <h2
        className="text-dark fw-bold mt-lg-5 text-md-center mt-4 text-start"
        style={{ fontSize: 32 }}
      >
        Forgot your password?
      </h2>
      <Stack
        className="w-100 mt-3"
        style={{ maxWidth: 480 }}
        gap={1}
      >
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={sendInstructions}
        >
          {({ errors, touched }: FormikProps<FormValues>) => (
            <FormikForm className="text-start">
              <Form.Group
                className="mb-2"
                controlId="email"
              >
                <Form.Label>Email Address</Form.Label>
                <Field
                  name="email"
                  type="email"
                  as={Form.Control}
                  isInvalid={touched.email && !!errors.email}
                />
                <ErrorMessage name="email">
                  {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
                </ErrorMessage>
              </Form.Group>

              <SimpleButton
                type="submit"
                className="w-100 my-3"
                variant="primary"
                disabled={loading}
              >
                {loading && <span className="spinner-border spinner-border-sm"></span>}
                Send instructions
              </SimpleButton>

              {message && (
                <div className="form-group">
                  <div
                    className="alert alert-danger"
                    role="alert"
                  >
                    {message}
                  </div>
                </div>
              )}
            </FormikForm>
          )}
        </Formik>

        <div className="d-flex justify-content-center align-items-center my-3">
          <span className="ff-inter fs-small text-secondary">Don't have an account?</span>
          <button
            onClick={() => navigate('/users/register')}
            className="btn btn-link fs-small fw-bolder text-uppercase link-primary link-underline-opacity-0 ms-2 p-0"
          >
            Sign Up
          </button>
        </div>
      </Stack>
    </FadeIn>
  );
};

export default ForgotPassword;
